import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Optional,
    Output,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { PerPersonnelQueryModel } from '../../personnel-operations/model/per-personnel-query-model';
import { PersonnelPickerContentComponent } from './personnel-picker-content.component';
import { HrPersonnelPickerReplaySubjectService } from '../service/hr-personnel-picker-replay-subject.service';
import { PerPersonnelModel } from '../../personnel-operations/model/per-personnel-model';
import { UiTranslateLoaderService } from 'ui-shared';
import { PerPersonnelSearchQueryModel } from '../../personnel-operations/model/hr-per-personnel-search-query-model';

export const PER_PICKER_COMP_INJECT_KEY = 'ExtendHrPerPicker';

@Component({
    selector: 'personnel-picker',
    templateUrl: './personnel-picker.component.html',
})
export class PersonnelPickerComponent implements OnInit {
    /** Fields for Extended Component Inject */
    @ViewChild('extendedCompContainer', { read: ViewContainerRef, static: true })
    public extendedCompContainerRef: ViewContainerRef | any;
    public extendedCompRef: ComponentRef<any> | any;
    @Input() enableExtendedComponentInject: boolean = true;

    /** Content Transfer Fields */
    @ViewChild(PersonnelPickerContentComponent) contentComponent: PersonnelPickerContentComponent;
    private _personnelPicker: PerPersonnelModel[];
    @Input() set personnelPicker(val: PerPersonnelModel[]) {
        if (val === this._personnelPicker) return;
        this._personnelPicker = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.personnelPicker = val;
        }
    }

    get personnelPicker(): any {
        return this._personnelPicker;
    }

    private _disabled: boolean = false;
    @Input() set disabled(val: boolean) {
        if (val === this._disabled) return;
        this._disabled = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.disabled = val;
        }
    }

    get disabled(): boolean {
        return this._disabled;
    }

    private _queryModel: PerPersonnelQueryModel;
    @Input() set queryModel(val: PerPersonnelQueryModel) {
        if (val === this._queryModel) return;
        this._queryModel = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.queryModel = val;
        }
    }

    get queryModel(): PerPersonnelQueryModel {
        return this._queryModel;
    }

    private _selectionMode: 'single' | 'multiple' | 'select-all';
    @Input() set selectionMode(val: 'single' | 'multiple' | 'select-all') {
        if (val === this._selectionMode) return;
        this._selectionMode = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.selectionMode = val;
        }
    }

    get selectionMode(): 'single' | 'multiple' | 'select-all' {
        return this._selectionMode;
    }

    private _showSelectedPerCount: boolean = false;
    @Input() set showSelectedPerCount(val: boolean) {
        if (val === this._showSelectedPerCount) return;
        this._showSelectedPerCount = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.showSelectedPerCount = val;
        }
    }

    get showSelectedPerCount(): boolean {
        return this._showSelectedPerCount;
    }

    private _authorizationRoleTypeCode: string = null;
    @Input() set authorizationRoleTypeCode(val: string) {
        if (val === this._authorizationRoleTypeCode) return;
        this._authorizationRoleTypeCode = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.authorizationRoleTypeCode = val;
        }
    }

    get authorizationRoleTypeCode(): string {
        return this._authorizationRoleTypeCode;
    }

    private _searchable: boolean = false;
    @Input() set searchable(val: boolean) {
        if (val === this._searchable) return;
        this._searchable = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.searchable = val;
        }
    }

    get searchable(): boolean {
        return this._searchable;
    }

    private _searchQueryModel: PerPersonnelSearchQueryModel;
    @Input() set searchQueryModel(val: PerPersonnelSearchQueryModel) {
        if (val === this._searchQueryModel) return;
        this._searchQueryModel = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.authorizationRoleTypeCode = val;
        }
    }

    get searchQueryModel(): PerPersonnelSearchQueryModel {
        return this._searchQueryModel;
    }

    private _addButtonLabel: string = this.translateService.instant('addSelected');
    @Input() set addButtonLabel(val: string) {
        if (val === this._addButtonLabel) return;
        this._addButtonLabel = val;
        if (this.extendedCompRef) {
            this.extendedCompRef.instance.addButtonLabel = val;
        }
    }

    get addButtonLabel(): string {
        return this._addButtonLabel;
    }

    @Output() valueEvent = new EventEmitter<any>();
    @Output() onSearchPersonnelSelected = new EventEmitter<any>();
    @Input() isIdentityNumberRequired = false;
    @Input() showTopOrg = false;
    private _searchResult: string;

    @Input()
    set searchResult(value: string) {
        this._searchResult = value;
        this.searchResultChange.emit(this._searchResult);
    }

    get searchResult(): string {
        return this._searchResult;
    }

    @Output() searchResultChange: EventEmitter<string> = new EventEmitter<string>();
    @Input() showSelectedPersonnel: boolean = false;
    @Input() rowsPerPageOptions = [];
    constructor(
        protected cdr: ChangeDetectorRef,
        protected perPickerReplaySubjectService: HrPersonnelPickerReplaySubjectService,
        protected translateService: UiTranslateLoaderService,
        @Optional() @Inject(PER_PICKER_COMP_INJECT_KEY) public extendedComponent?: Component,
    ) {}

    ngOnInit(): void {
        if (this.extendedComponent) {
            this.loadComponent(this.extendedComponent);
        }
    }

    /** Functions for Extended Component Inject */
    loadComponent(extComponent: any) {
        this.extendedCompRef = this.extendedCompContainerRef.createComponent(extComponent);
        this.extendedCompRef.instance.selectionMode = this.selectionMode;
        this.extendedCompRef.instance.personnelPicker = this.personnelPicker;
        this.extendedCompRef.instance.disabled = this.disabled;
        this.extendedCompRef.instance.queryModel = this.queryModel;
        this.extendedCompRef.instance.showSelectedPerCount = this.showSelectedPerCount;
        this.extendedCompRef.instance.authorizationRoleTypeCode = this.showSelectedPerCount;
        this.extendedCompRef.instance.searchable = this.showSelectedPerCount;
        this.extendedCompRef.instance.searchQueryModel = this.searchQueryModel;
        this.extendedCompRef.instance.valueEvent = this.valueEvent;
        this.extendedCompRef.instance.onSearchPersonnelSelected = this.onSearchPersonnelSelected;
        this.cdr.detectChanges();
    }

    onShow() {
        if (this.extendedComponent) {
            this.perPickerReplaySubjectService.startOnShowProcess();
        } else {
            this.contentComponent.onShow();
        }
    }
    onSearchPersonnelSelectedF(e) {
        this.onSearchPersonnelSelected.emit(e);
    }
    onFilteredPerSelected(e) {
        this.searchResult = e;
    }
}
